import { useAppSelector } from 'app/hooks';
import { useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import {
  buildSystemComparisonKey,
  buildSystemComparisonPayload,
  SystemComparisonSummariesResponseType,
  SystemComparisonSummariesType
} from 'utils/system-comparison-summaries';

const summariesCache = new Map<string, SystemComparisonSummariesType>();
const SUMMARIES_REQUEST_DELAY_MS = 1500;

export default function useSystemComparisonSummaries() {
  const systemsToCompare = useAppSelector(
    (state) => state.systems.systemsToCompare
  );
  const [summaries, setSummaries] = useState<SystemComparisonSummariesType>({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const comparisonKey = useMemo(
    () => buildSystemComparisonKey(systemsToCompare),
    [systemsToCompare]
  );

  useEffect(() => {
    if (systemsToCompare.length < 2) {
      setSummaries({});
      setIsLoading(false);
      setHasError(false);
      return undefined;
    }

    const cached = summariesCache.get(comparisonKey);
    if (cached) {
      setSummaries(cached);
      setIsLoading(false);
      setHasError(false);
      return undefined;
    }

    const controller = new AbortController();
    setSummaries({});
    setIsLoading(true);
    setHasError(false);

    const timer = setTimeout(() => {
      axios
        .post<SystemComparisonSummariesResponseType>(
          'ai/compare-systems',
          buildSystemComparisonPayload(systemsToCompare),
          { signal: controller.signal }
        )
        .then((response) => {
          summariesCache.set(comparisonKey, response.data.summaries);
          setSummaries(response.data.summaries);
          setIsLoading(false);
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setHasError(true);
          setIsLoading(false);
        });
    }, SUMMARIES_REQUEST_DELAY_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [comparisonKey]);

  return { summaries, isLoading, hasError };
}
